import React from "react";
import { Chip, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

interface TinyValueComponentProps {
  label?: string;
  value: string;
  clipboard?: boolean;
  color?: any;
  autocolor?: boolean;
  prefix?: string; // Prefix that won't be copied (e.g., "tid:")
  cropText?: boolean; // Whether to crop text to first 3 and last 3 characters
}

// Define 16 pastel colors
const pastelColors = [
  "#FFB3BA",
  "#FFDFBA",
  "#FFFFBA",
  "#BAFFC9",
  "#BAE1FF",
  "#E6B0AA",
  "#D7BDE2",
  "#A9CCE3",
  "#A3E4D7",
  "#F9E79F",
  "#F5CBA7",
  "#D5DBDB",
  "#FADBD8",
  "#D4E6F1",
  "#D0ECE7",
  "#FDEDEC",
];

// Function to get a color based on the value
const getColorFromValue = (value: string) => {
  const hash = Array.from(value).reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0
  );
  return pastelColors[hash % pastelColors.length]; // Ensures same value gets the same color
};

// Function to crop text to first 3 and last 3 characters
const cropText = (text: string): string => {
  if (!text || text.length <= 6) {
    return text;
  }
  const first3 = text.substring(0, 3);
  const last3 = text.substring(text.length - 3);
  return `${first3}...${last3}`;
};

export const TinyValueComponent: React.FC<TinyValueComponentProps> = ({
  label,
  value,
  clipboard,
  color,
  autocolor,
  prefix,
  cropText: shouldCropText,
}) => {
  const handleCopy = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(value);
  };

  const chipColor = autocolor ? getColorFromValue(value) : color;
  const displayValue = shouldCropText ? cropText(value) : value;

  return (
    <Chip
      size="small"
      label={
        <Tooltip title={`Copy to clipboard: ${value}`}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {prefix && <span style={{ opacity: 0.7 }}>{prefix}</span>}
            <b>{label}</b>
            {displayValue}
            {clipboard && (
              <ContentCopyIcon sx={{ fontSize: 12, opacity: 0.6 }} />
            )}
          </span>
        </Tooltip>
      }
      onClick={handleCopy}
      sx={{
        fontSize: "0.6rem",
        height: 16,
        padding: "0 4px",
        minWidth: "auto",
        cursor: "pointer",
        backgroundColor: chipColor,
        color: "black", // Ensuring contrast for readability
      }}
    />
  );
};
